import { useInfiniteQuery } from "@tanstack/react-query";
import { Globe2, LayoutGrid, MapPin, Search, Sun } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrgId } from "../api/org";
import { Alert, Badge, Button, Card, Empty, PageHead, Segmented, Select, Spinner } from "../components/ui";
import { date, money, num } from "../lib/format";

type Lead = S["Lead"];

export function leadState(lead: Lead): { label: string; tone?: "ok" | "warn" | "danger" | "info" } {
  if (lead.job_status === "queued" || lead.job_status === "running" || lead.public_status === "processing")
    return { label: "Designing", tone: "info" };
  if (lead.job_status === "failed" || lead.public_status === "failed") return { label: "Failed", tone: "danger" };
  if (lead.status === "sold") return { label: "Sold", tone: "ok" };
  if (lead.public_status === "completed" || lead.quote_id) return { label: "Quoted", tone: "ok" };
  if (lead.public_status === "ready" || lead.kw_dc) return { label: "Designed", tone: "warn" };
  return { label: lead.status === "draft" ? "Draft" : lead.status };
}

export function leadName(lead: Lead): string | null {
  const c = lead.contact;
  if (!c) return null;
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
  return name || c.email || null;
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function Designs() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | S["DesignStatus"]>("");
  const [source, setSource] = useState<"all" | S["LeadSource"]>("all");
  const query = useDebounced(q.trim());

  const leads = useInfiniteQuery({
    queryKey: ["leads-infinite", orgId, query, status, source],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET("/v1/orgs/{org_id}/leads", {
          params: {
            path: { org_id: orgId },
            query: {
              limit: 25,
              cursor: pageParam,
              q: query || undefined,
              status: status || undefined,
              source: source === "all" ? undefined : source,
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    placeholderData: (prev) => prev,
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.items.some((i) => i.job_status === "queued" || i.job_status === "running")) ? 5000 : false,
  });
  const items = leads.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = !!(query || status || source !== "all");

  return (
    <>
      <PageHead
        title="Designs & leads"
        description="Every roof designed for you — from your website calculator or the dashboard — with the customer who asked."
      />
      <Card pad={false}>
        <div className="toolbar">
          <div className="input-icon">
            <Search />
            <input className="input" placeholder="Search address, name or email" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <Segmented
            value={source}
            onChange={setSource}
            options={[
              { value: "all", label: "All" },
              { value: "website", label: "Website" },
              { value: "dashboard", label: "Dashboard" },
            ]}
          />
          <div style={{ width: 150 }}>
            <Select
              value={status}
              onChange={setStatus}
              options={[
                { value: "", label: "Any status" },
                { value: "draft", label: "Draft" },
                { value: "proposed", label: "Proposed" },
                { value: "sold", label: "Sold" },
                { value: "archived", label: "Archived" },
              ]}
            />
          </div>
        </div>
        {leads.isLoading ? (
          <Spinner />
        ) : leads.error ? (
          <div className="card-body">
            <Alert tone="danger">{errorMessage(leads.error)}</Alert>
          </div>
        ) : !items.length ? (
          filtered ? (
            <Empty icon={<Search />} title="No matches">
              Nothing matches these filters.
            </Empty>
          ) : (
            <Empty icon={<LayoutGrid />} title="No designs yet" action={<Link className="btn btn-primary" to="/get-started">Put the calculator on your site</Link>}>
              When a visitor asks for a quote on your website, the roof design and their details appear here.
            </Empty>
          )
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th className="hide-sm">Lead</th>
                  <th className="hide-sm">Status</th>
                  <th className="num hide-sm">System</th>
                  <th className="num hide-sm">Price</th>
                  <th className="num hide-sm">Date</th>
                </tr>
              </thead>
              <tbody>
                {items.map((lead) => {
                  const state = leadState(lead);
                  const name = leadName(lead);
                  return (
                    <tr key={lead.design_id} className="clickable" onClick={() => navigate(`/designs/${lead.design_id}`)}>
                      <td style={{ maxWidth: 360 }}>
                        <div className="row" style={{ gap: 10, flexWrap: "nowrap" }}>
                          <div className="empty-icon" style={{ width: 34, height: 34, borderRadius: 10, display: "grid", placeItems: "center", background: "var(--grad-soft)", color: "var(--primary)", flex: "none" }}>
                            {lead.source === "website" ? <Globe2 size={16} /> : <MapPin size={16} />}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div className="cell-main ellipsis">
                              <Link to={`/designs/${lead.design_id}`} onClick={(e) => e.stopPropagation()} style={{ color: "inherit" }}>
                                {lead.address || lead.design_name}
                              </Link>
                            </div>
                            <div className="cell-sub ellipsis">
                              {lead.source === "website" ? "Website calculator" : "Dashboard"} · {name ?? lead.design_name}
                            </div>
                            <div className="show-sm row" style={{ gap: 8, marginTop: 6 }}>
                              <Badge tone={state.tone}>{state.label}</Badge>
                              {lead.kw_dc ? <span className="small muted">{num(lead.kw_dc, 1)} kW · {lead.panel_count} panels</span> : null}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hide-sm" style={{ maxWidth: 220 }}>
                        {name ? (
                          <>
                            <div className="cell-main ellipsis">{name}</div>
                            <div className="cell-sub ellipsis">{lead.contact?.email || lead.contact?.phone || ""}</div>
                          </>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="hide-sm">
                        <Badge tone={state.tone}>{state.label}</Badge>
                      </td>
                      <td className="num hide-sm">
                        {lead.kw_dc ? (
                          <>
                            <div className="cell-main">{num(lead.kw_dc, 1)} kW</div>
                            <div className="cell-sub">
                              <Sun size={11} style={{ verticalAlign: -1 }} /> {lead.panel_count} panels
                            </div>
                          </>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="num hide-sm">
                        {lead.quote_total != null ? (
                          <>
                            <div className="cell-main">{money(lead.quote_total, lead.currency ?? "USD")}</div>
                            <div className="cell-sub">net {money(lead.quote_net_cost, lead.currency ?? "USD")}</div>
                          </>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="num hide-sm">
                        <div>{date(lead.created_at)}</div>
                        <div className="cell-sub">{new Date(lead.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {leads.hasNextPage && (
          <div className="card-foot" style={{ justifyContent: "center" }}>
            <Button loading={leads.isFetchingNextPage} onClick={() => leads.fetchNextPage()}>
              Load more
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
